import {useMessage} from '@coolcolduk/ui-mui';
import {resolveTradeProfitLoss, Trade, TradeDirection, TradeStatus} from '@cp/model';
import {useCallback, useEffect, useState} from 'react';
import {deleteTrade} from '../../../api/trade/deleteTrade';
import {updateTrade} from '../../../api/trade/updateTrade';
import AdminTradeTrades from './AdminTradeTrades';
import type {EditableTrade} from './AdminTradeTypes';
import useTrades from '../../../hooks/trades/useTrades';

function tradePositionSizeForPnL(trade: Trade): number {
  return trade.positionSize !== undefined && Number.isFinite(trade.positionSize) && trade.positionSize > 0
    ? trade.positionSize
    : 1;
}

export interface AdminTradeTradesSectionProps {
  sessionId: string;
  onJournalSessionsRefetch: () => void;
}

export default function AdminTradeTradesSection({sessionId, onJournalSessionsRefetch}: AdminTradeTradesSectionProps) {
  const {showMessage} = useMessage();
  const pagination = useTrades(sessionId);

  const [trades, setTrades] = useState<EditableTrade[]>([]);
  const [savingId, setSavingId] = useState<string | undefined>(undefined);
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (pagination.error !== undefined) {
      showMessage(pagination.error?.message ?? 'Unknown error', 6000, 'error');
    }
  }, [pagination.error, showMessage]);

  useEffect(() => {
    setTrades((prev) => {
      const prevById = new Map(prev.map((t) => [t.id, t]));
      return pagination.data.map((t) => {
        const existing = prevById.get(t.id);
        return {...t, isEditing: existing?.isEditing ?? false} as EditableTrade;
      });
    });
  }, [pagination.data]);

  const handleEditToggle = useCallback((id: string, editing: boolean) => {
    setTrades((prev) => prev.map((trade) => (trade.id === id ? {...trade, isEditing: editing} : trade)));
  }, []);

  const handleFieldChange = useCallback(
    <K extends keyof EditableTrade>(id: string, field: K, value: EditableTrade[K]) => {
      setTrades((prev) => prev.map((trade) => (trade.id === id ? {...trade, [field]: value} : trade)));
    },
    [],
  );

  const handleSave = async (trade: EditableTrade) => {
    if (!trade.symbol.trim()) {
      showMessage('Symbol is required', 4000, 'error');
      return;
    }
    if (!Number.isFinite(trade.entryPrice) || Number.isNaN(trade.entryPrice)) {
      showMessage('Entry (open) price must be a valid number', 4000, 'error');
      return;
    }
    if (
      trade.status !== TradeStatus.OPEN &&
      (trade.exitPrice === undefined || !Number.isFinite(trade.exitPrice) || trade.exitPrice <= 0)
    ) {
      showMessage('Exit (close) price is required for closed trades (must be > 0)', 4000, 'error');
      return;
    }
    if (trade.status !== TradeStatus.OPEN && !Number.isFinite(trade.profitLoss)) {
      showMessage('P/L override must be a valid number', 4000, 'error');
      return;
    }
    setSavingId(trade.id);
    try {
      const update: {
        symbol: string;
        direction: TradeDirection;
        status: TradeStatus;
        entryPrice: number;
        sessionId?: string;
        tradeId?: string;
        exitPrice?: number;
        closedAt?: number;
        profitLoss?: number;
        tags: string[];
      } = {
        symbol: trade.symbol.trim(),
        direction: trade.direction,
        status: trade.status,
        entryPrice: trade.entryPrice,
        tags: trade.tags,
      };

      const trimmedSession = trade.sessionId?.trim();
      if (trimmedSession !== undefined && trimmedSession !== '') {
        update.sessionId = trimmedSession;
      }
      const trimmedTradeId = trade.tradeId?.trim();
      if (trimmedTradeId !== undefined && trimmedTradeId !== '') {
        update.tradeId = trimmedTradeId;
      }
      if (trade.exitPrice !== undefined && Number.isFinite(trade.exitPrice) && trade.exitPrice > 0) {
        update.exitPrice = trade.exitPrice;
      }

      const sizeForPnL = tradePositionSizeForPnL(trade);
      if (trade.status === TradeStatus.OPEN) {
        await updateTrade(trade.id, {
          ...update,
          exitPrice: 0,
          closedAt: 0,
          profitLoss: 0,
        });
      } else {
        const closedAtMs =
          trade.closedAt !== undefined && Number.isFinite(trade.closedAt) && trade.closedAt > 0
            ? trade.closedAt
            : Date.now();
        update.closedAt = closedAtMs;
        const finalPl = resolveTradeProfitLoss({...trade, positionSize: sizeForPnL});
        update.profitLoss = finalPl ?? 0;
        await updateTrade(trade.id, update);
      }
      setTrades((prev) => prev.map((t) => (t.id === trade.id ? {...trade, isEditing: false} : t)));
      onJournalSessionsRefetch();
      void pagination.refetch();
      showMessage('Trade updated', 4000, 'success');
    } catch (error) {
      console.error('Failed to update trade', error);
      showMessage('Failed to update trade', 6000, 'error');
    } finally {
      setSavingId(undefined);
    }
  };

  const handleDelete = async (id: string) => {
    setSavingId(id);
    try {
      await deleteTrade(id);
      setTrades((prev) => prev.filter((trade) => trade.id !== id));
      onJournalSessionsRefetch();
      void pagination.refetch();
      showMessage('Trade deleted', 4000, 'success');
    } catch (error) {
      console.error('Failed to delete trade', error);
      showMessage('Failed to delete trade', 6000, 'error');
    } finally {
      setSavingId(undefined);
    }
  };

  return (
    <AdminTradeTrades
      trades={trades}
      loading={pagination.loading}
      pageDepth={pagination.pageIndex}
      hasNextPage={pagination.canGoNext}
      savingId={savingId}
      deleteDialogOpen={deleteId !== undefined}
      deleteDialogLoading={savingId === deleteId}
      onReloadTrades={pagination.refetch}
      onTradesPrev={pagination.handlePrevPage}
      onTradesNext={pagination.handleNextPage}
      onEditToggle={handleEditToggle}
      onFieldChange={handleFieldChange}
      onSaveTrade={(trade) => void handleSave(trade)}
      onRequestDelete={(id) => setDeleteId(id)}
      onDeleteDialogConfirm={() => {
        if (deleteId === undefined) return;
        void handleDelete(deleteId);
        setDeleteId(undefined);
      }}
      onDeleteDialogCancel={() => setDeleteId(undefined)}
    />
  );
}
